import AtossLogo from '@/assets/integrations/atoss.png';
import TimendoLogo from '@/assets/integrations/timendo.png';
import OutlookLogo from '@/assets/integrations/outlook.png';
import GoogleCalendarLogo from '@/assets/integrations/googlecalendar.png';
import ExchangeLogo from '@/assets/integrations/Exchange.webp';
import DatevLogo from '@/assets/integrations/datev.png';
import HubspotLogo from '@/assets/integrations/hubspot.png';
import SalesforceLogo from '@/assets/integrations/salesforce.png';
import ZapierLogo from '@/assets/integrations/zapier.png';
import SHDLogo from '@/assets/integrations/shd.png';
import CaratLogo from '@/assets/integrations/carat.png';
import FurnplanLogo from '@/assets/integrations/furnplan.png';
import TrickparkLogo from '@/assets/integrations/trickpark.svg';
import MicrosoftLogo from '@/assets/integrations/microsoft.svg';


import {
  disableEwsForPartnerRequest,
  disableMicrosoftSsoForPartnerRequest,
  disableOffice365MailForPartnerRequest,
  enableMicrosoftSsoForPartnerRequest,
  enableOffice365MailForPartnerRequest,
  disableGoogleForPartnerRequest,
  disableOutlookForPartnerRequest,
  enableEwsForPartnerRequest,
  enableGoogleForPartnerRequest,
  enableOutlookForPartnerRequest,
} from '@/services/api/integration';
import {
  installTrickparkRequest,
  uninstallTrickparkRequest,
} from '@/services/api/trickpark';
import { PartnerUserIntegrationModel } from '@/typings/models/PartnerUserIntegrationModel';
import { PARTNER_ROLLOUT_FLAGS, PartnerRolloutFlag } from '@/constants/localFeatureFlags';
import { processAPIResponseNow } from '@/utils/request';
import { message, Modal } from 'antd';

export const INTEGRATION_TYPE_OUTLOOK = 'outlook';
export const INTEGRATION_TYPE_OFFICE365_MAIL = 'office365_mail';
export const INTEGRATION_TYPE_MICROSOFT_SSO = 'microsoft_sso';
export const INTEGRATION_TYPE_EXCHANGE_EWS = 'exchange_ews_calendar';
export const INTEGRATION_TYPE_ATOSS = 'atoss';
export const INTEGRATION_TYPE_TIMENDO = 'timendo';
export const INTEGRATION_TYPE_GOOGLE_CALENDAR = 'google';
export const INTEGRATION_TYPE_DATEV = 'datev';
export const INTEGRATION_TYPE_HUBSPOT = 'hubspot';
export const INTEGRATION_TYPE_SALESFORCE = 'salesforce';
export const INTEGRATION_TYPE_ZAPIER = 'zapier';
export const INTEGRATION_TYPE_SHD = 'shd';
export const INTEGRATION_TYPE_CARAT = 'carat';
export const INTEGRATION_TYPE_FURNPLAN = 'furnplan';
export const INTEGRATION_TYPE_TRICKPARK = 'trickpark';

/** API may return "google" for Google Calendar; normalize to our constant for lookups */
export const INTEGRATION_TYPE_ALIASES: Record<string, string> = {
  google: INTEGRATION_TYPE_GOOGLE_CALENDAR,
};

/** Dark-launched marketplace cards: no entry = visible for every partner. */
export const INTEGRATION_TYPE_ROLLOUT_FLAGS: Record<string, PartnerRolloutFlag> = {
  [INTEGRATION_TYPE_MICROSOFT_SSO]: PARTNER_ROLLOUT_FLAGS.MICROSOFT_SSO,
};

export const INTEGRATION_TYPE_LOGOS = {
  [INTEGRATION_TYPE_OUTLOOK]: OutlookLogo,
  // Microsoft 365 has no dedicated asset yet; reuse the Outlook mark.
  [INTEGRATION_TYPE_OFFICE365_MAIL]: OutlookLogo,
  [INTEGRATION_TYPE_MICROSOFT_SSO]: MicrosoftLogo,
  // Microsoft Exchange (EWS) has no dedicated asset; reuse the Outlook/Microsoft logo.
  [INTEGRATION_TYPE_EXCHANGE_EWS]: ExchangeLogo,
  [INTEGRATION_TYPE_ATOSS]: AtossLogo,
  [INTEGRATION_TYPE_TIMENDO]: TimendoLogo,
  [INTEGRATION_TYPE_GOOGLE_CALENDAR]: GoogleCalendarLogo,
  [INTEGRATION_TYPE_DATEV]: DatevLogo,
  [INTEGRATION_TYPE_HUBSPOT]: HubspotLogo,
  [INTEGRATION_TYPE_SALESFORCE]: SalesforceLogo,
  [INTEGRATION_TYPE_ZAPIER]: ZapierLogo,
  [INTEGRATION_TYPE_SHD]: SHDLogo,
  [INTEGRATION_TYPE_CARAT]: CaratLogo,
  [INTEGRATION_TYPE_FURNPLAN]: FurnplanLogo,
  [INTEGRATION_TYPE_TRICKPARK]: TrickparkLogo,
};

export const INTEGRATION_TYPE_INTERNAL_TAGS = {
  [INTEGRATION_TYPE_OUTLOOK]: {
    description: 'outlook_integration_description',
    manual: 'outlook_integration_manual',
  },
  [INTEGRATION_TYPE_OFFICE365_MAIL]: {
    description: 'office365_mail_integration_description',
    manual: 'office365_mail_integration_manual',
  },
  [INTEGRATION_TYPE_MICROSOFT_SSO]: {
    description: 'microsoft_sso_integration_description',
    manual: 'microsoft_sso_integration_manual',
  },
  [INTEGRATION_TYPE_EXCHANGE_EWS]: {
    description: 'ews-integration-description',
    manual: 'ews-integration-manual',
  },
  [INTEGRATION_TYPE_ATOSS]: {
    description: 'atoss_integration_description',
    manual: 'atoss_integration_manual',
  },
  [INTEGRATION_TYPE_TIMENDO]: {
    description: 'timendo_integration_description',
    manual: 'timendo_integration_manual',
  },
  [INTEGRATION_TYPE_GOOGLE_CALENDAR]: {
    description: 'googlecalendar_integration_description',
    manual: 'googlecalendar_integration_manual',
  },
  [INTEGRATION_TYPE_DATEV]: {
    description: 'datev_integration_description',
    manual: 'datev_integration_manual',
  },
  [INTEGRATION_TYPE_HUBSPOT]: {
    description: 'hubspot_integration_description',
    manual: 'hubspot_integration_manual',
  },
  [INTEGRATION_TYPE_SALESFORCE]: {
    description: 'salesforce_integration_description',
    manual: 'salesforce_integration_manual',
  },
  [INTEGRATION_TYPE_ZAPIER]: {
    description: 'zapier_integration_description',
    manual: 'zapier_integration_manual',
  },
  [INTEGRATION_TYPE_SHD]: {
    description: 'shd_integration_description',
    manual: 'shd_integration_manual',
  },
  [INTEGRATION_TYPE_CARAT]: {
    description: 'carat_integration_description',
    manual: 'carat_integration_manual',
  },
  [INTEGRATION_TYPE_FURNPLAN]: {
    description: 'furnplan_integration_description',
    manual: 'furnplan_integration_manual',
  },
  [INTEGRATION_TYPE_TRICKPARK]: {
    description: 'trickpark_integration_description',
    manual: 'trickpark_integration_manual',
  },
};

/**
 * Types whose per-user link is a manually entered external id (the Integrations
 * section of the user drawer, persisted to users_integrations.integration_id).
 * The backend is the source of truth via `supports_user_mapping` on
 * GET /integrations — this list only backstops responses predating that flag.
 */
export const USER_MAPPABLE_INTEGRATION_TYPES = [
  INTEGRATION_TYPE_ATOSS,
  INTEGRATION_TYPE_TIMENDO,
];

export const supportsUserMapping = (integration: {
  type?: string;
  integration_type?: string;
  supports_user_mapping?: boolean;
}): boolean => {
  if (typeof integration?.supports_user_mapping === 'boolean') {
    return integration.supports_user_mapping;
  }

  const type = integration?.type || integration?.integration_type || '';
  return USER_MAPPABLE_INTEGRATION_TYPES.includes(type);
};

/**
 * Types each user connects for themselves through the integration's own flow
 * (OAuth or service credentials). Their users_integrations rows carry the
 * connection state, so they are shown read-only per user — never edited by hand.
 * Mirrors `supports_user_connection` on GET /integrations.
 */
export const USER_CONNECTABLE_INTEGRATION_TYPES = [
  INTEGRATION_TYPE_OUTLOOK,
  INTEGRATION_TYPE_GOOGLE_CALENDAR,
  INTEGRATION_TYPE_EXCHANGE_EWS,
];

export const supportsUserConnection = (integration: {
  type?: string;
  integration_type?: string;
  supports_user_connection?: boolean;
}): boolean => {
  if (typeof integration?.supports_user_connection === 'boolean') {
    return integration.supports_user_connection;
  }

  const type = integration?.type || integration?.integration_type || '';
  return USER_CONNECTABLE_INTEGRATION_TYPES.includes(type);
};

export const INTEGRATION_TYPE_SETTINGS = {
  [INTEGRATION_TYPE_OUTLOOK]: {},
  [INTEGRATION_TYPE_OFFICE365_MAIL]: {},
  [INTEGRATION_TYPE_MICROSOFT_SSO]: {},
  [INTEGRATION_TYPE_EXCHANGE_EWS]: {},
  [INTEGRATION_TYPE_GOOGLE_CALENDAR]: {},
  [INTEGRATION_TYPE_ATOSS]: {
    sendTicketToInstall: true,
  },
  [INTEGRATION_TYPE_TIMENDO]: {
    sendTicketToInstall: true,
  },
};


export const installIntegration = (integration: PartnerUserIntegrationModel, data) => {
  return new Promise((resolve, reject) => {
    switch (integration.integration_type) {
      case INTEGRATION_TYPE_OUTLOOK:
        enableOutlookForPartnerRequest({data}).then((Response) => {
          processAPIResponseNow(Response, {
            onSuccess(response) {
              if (response.auth_url) return window.location.href = response.auth_url;
              resolve();
            },
            // Without this the promise never settles on a failed enable, so the
            // Install button spins forever and the reason is never shown.
            // Rejecting (rather than resolving) also stops the caller reloading
            // the page over the error message.
            onError(error) {
              message.error(error?.message || error?.error?.message);
              reject(error);
            },
          }, 'data');
        });
        return;
      case INTEGRATION_TYPE_OFFICE365_MAIL:
        enableOffice365MailForPartnerRequest({data}).then((Response) => {
          processAPIResponseNow(Response, {
            onSuccess(response) {
              if (response.auth_url) return window.location.href = response.auth_url;
              resolve();
            },
            // Without this the promise never settles on a failed enable, so the
            // Install button spins forever and the reason is never shown.
            // Rejecting (rather than resolving) also stops the caller reloading
            // the page over the error message.
            onError(error) {
              message.error(error?.message || error?.error?.message);
              reject(error);
            },
          }, 'data');
        });
        return;
      case INTEGRATION_TYPE_MICROSOFT_SSO:
        enableMicrosoftSsoForPartnerRequest({data}).then((Response) => {
          processAPIResponseNow(Response, {
            onSuccess(response) {
              if (response.auth_url) return window.location.href = response.auth_url;
              resolve();
            }
          }, 'data');
        });
        return;
      case INTEGRATION_TYPE_GOOGLE_CALENDAR:
        enableGoogleForPartnerRequest({data}).then((Response) => {
          processAPIResponseNow(Response, {
            onSuccess(response) {
              if (response.auth_url) return window.location.href = response.auth_url;
              resolve();
            },
            onError(error) {
              message.error(error?.message || error?.error?.message);
              reject(error);
            },
          }, 'data');
        });
        return;
      case INTEGRATION_TYPE_EXCHANGE_EWS:
        // Credential-based (no OAuth redirect) — enable immediately with the submitted config.
        enableEwsForPartnerRequest({data}).then((Response) => {
          processAPIResponseNow(Response, {
            onSuccess() {
              resolve();
            },
            onError(error) {
              message.error(error?.message || error?.error?.message);
              reject();
            },
          }, 'data');
        });
        return;
      case INTEGRATION_TYPE_TRICKPARK:
        // One-click install (no form) — the backend generates the endpoint id and API key.
        installTrickparkRequest().then((Response) => {
          processAPIResponseNow(Response, {
            onSuccess() {
              resolve();
            },
            onError(error) {
              message.error(error?.message || error?.error?.message);
              reject();
            },
          }, 'data');
        });
        return;
      default:
        resolve();
    }
  });
};

export const disableIntegration = (integration: PartnerUserIntegrationModel, intl: any) => {
  return new Promise((resolve, reject) => {
    switch (integration.integration_type) {
      case INTEGRATION_TYPE_OUTLOOK:
        Modal.confirm({
          title: intl.formatMessage({
            id: 'pages.integrations.disable_confirmation',
          }),
          onOk: () =>
            disableOutlookForPartnerRequest().then((Response) => {
              resolve();
            }),
          onCancel: () => reject(),
        });
        return;
      case INTEGRATION_TYPE_OFFICE365_MAIL:
        Modal.confirm({
          title: intl.formatMessage({
            id: 'pages.integrations.disable_confirmation',
          }),
          onOk: () =>
            disableOffice365MailForPartnerRequest().then(() => {
              resolve();
            }),
          onCancel: () => reject(),
        });
        return;
      case INTEGRATION_TYPE_MICROSOFT_SSO:
        Modal.confirm({
          title: intl.formatMessage({
            id: 'pages.integrations.disable_confirmation',
          }),
          // SSO users keep their accounts and last-stamped roles; they fall
          // back to password/reset-password login once the binding is gone.
          content: intl.formatMessage({
            id: 'pages.integrations.microsoft_sso.disable_note',
            defaultMessage:
              'People will no longer be able to sign in with Microsoft. Existing users keep their accounts and current roles and can use password login instead.',
          }),
          onOk: () =>
            disableMicrosoftSsoForPartnerRequest().then(() => {
              resolve();
            }),
          onCancel: () => reject(),
        });
        return;
      case INTEGRATION_TYPE_GOOGLE_CALENDAR:
        Modal.confirm({
          title: intl.formatMessage({
            id: 'pages.integrations.disable_confirmation',
          }),
          onOk: () =>
            disableGoogleForPartnerRequest().then((Response) => {
              resolve();
            }),
          onCancel: () => reject(),
        });
        return;
      case INTEGRATION_TYPE_EXCHANGE_EWS:
        Modal.confirm({
          title: intl.formatMessage({
            id: 'pages.integrations.disable_confirmation',
          }),
          onOk: () =>
            disableEwsForPartnerRequest().then((Response) => {
              resolve();
            }),
          onCancel: () => reject(),
        });
        return;
      case INTEGRATION_TYPE_TRICKPARK:
        Modal.confirm({
          title: intl.formatMessage({
            id: 'pages.integrations.disable_confirmation',
          }),
          onOk: () =>
            uninstallTrickparkRequest().then(() => {
              resolve();
            }),
          onCancel: () => reject(),
        });
        return;
      default:
        resolve();
    }
  });
};

export const getOAuthCallbackRedirectPath = (integrationType: string): string => {
  switch (integrationType) {
    case INTEGRATION_TYPE_OFFICE365_MAIL:
      // Consent is step one; the admin lands on the detail page to sync and
      // enable mailboxes.
      return '/integrations/apps/office365_mail';
    case INTEGRATION_TYPE_MICROSOFT_SSO:
      // After consent the admin configures the group → role mapping here.
      return '/integrations/apps/microsoft_sso';
    case INTEGRATION_TYPE_OUTLOOK:
      return '/calendar';
    case INTEGRATION_TYPE_GOOGLE_CALENDAR:
      return '/calendar';
    default:
      return '/profile';
  }
};
